import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, useApi, type PlatformAccount, type TemporaryPassword } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { useSave } from "@ncp/ui";

// ---------------------------------------------------------------- permissions & roles

export interface PermissionInfo { key: string; area: string; name: string; description: string; dangerous: boolean }
export interface PermissionArea { area: string; permissions: PermissionInfo[] }

export interface Role {
  id: string;
  key: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  permissions: string[];
  effectivePermissions: string[];
  isBuiltIn: boolean;
  rank: number;
  assignable: boolean;
  color: string;
  enabled: boolean;
  members: number;
  updatedAt: string;
  departmentKey?: string | null;
}

export interface RoleInput {
  key?: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  permissions: string[];
  rank: number;
  assignable: boolean;
  color?: string | null;
  enabled: boolean;
  departmentKey?: string | null;
}

export function usePermissionCatalogue() {
  const api = useApi();
  return useQuery({ queryKey: ["platform", "permissions"], queryFn: () => api.get<PermissionArea[]>("/api/platform/permissions"), staleTime: 5 * 60_000 });
}

export function useRoles() {
  const api = useApi();
  return useQuery({ queryKey: ["platform", "roles"], queryFn: () => api.get<Role[]>("/api/platform/roles"), staleTime: 15_000 });
}

export function useRoleActions() {
  const api = useApi();
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["platform", "roles"] });
    void qc.invalidateQueries({ queryKey: ["platform", "users"] });
    void qc.invalidateQueries({ queryKey: ["me", "permissions"] });
  };
  return {
    create: useSave({ mutationFn: (input: RoleInput) => api.post<Role>("/api/platform/roles", input), onSuccess: invalidate }),
    update: useSave({ mutationFn: (input: { key: string } & RoleInput) => api.put<Role>(`/api/platform/roles/${input.key}`, input), onSuccess: invalidate }),
    remove: useSave({ mutationFn: (key: string) => api.delete<void>(`/api/platform/roles/${key}`), onSuccess: invalidate }),
    reset: useSave({ mutationFn: (key: string) => api.post<Role>(`/api/platform/roles/${key}/reset`), onSuccess: invalidate }),
  };
}

// ---------------------------------------------------------------- users

export interface DirectoryUser {
  id: string;
  displayName: string;
  displayNameAr?: string | null;
  email?: string | null;
  title?: string | null;
  kind: "Staff" | "External" | "System";
  /** Built-in roles from the user record (StaffRole / ExternalRole names). */
  roles: string[];
  /** Roles assigned through the console (role keys). */
  dynamicRoles: string[];
  isActive: boolean;
  isDemoPersona: boolean;
  lastLoginAt?: string | null;
  organizationName?: string | null;
  /** The identity provider the account signs in with, once linked to a directory identity. */
  identityProviderKey?: string | null;
  /** Roles (built-in or console) the provider's group mappings granted at the last sign-in. */
  directoryRoles?: string[] | null;
}

export function useDirectory(params: { q?: string; role?: string; kind?: "Staff" | "External"; includeInactive?: boolean }) {
  const api = useApi();
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.role) qs.set("role", params.role);
  if (params.kind) qs.set("kind", params.kind);
  if (params.includeInactive) qs.set("includeInactive", "true");
  return useQuery({ queryKey: ["platform", "users", Object.fromEntries(qs)], queryFn: () => api.get<DirectoryUser[]>(`/api/platform/users?${qs.toString()}`), staleTime: 10_000 });
}

export function useUserActions() {
  const api = useApi();
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["platform", "users"] });
    void qc.invalidateQueries({ queryKey: ["platform", "roles"] });
  };
  return {
    assign: useSave({ mutationFn: (input: { userId: string; roleKey: string; validUntil?: string | null }) => api.post<void>(`/api/platform/users/${input.userId}/roles`, { roleKey: input.roleKey, validUntil: input.validUntil ?? null }), onSuccess: invalidate }),
    unassign: useSave({ mutationFn: (input: { userId: string; roleKey: string }) => api.delete<void>(`/api/platform/users/${input.userId}/roles/${input.roleKey}`), onSuccess: invalidate }),
    setActive: useSave({ mutationFn: (input: { userId: string; active: boolean }) => api.post<void>(`/api/platform/users/${input.userId}/active?active=${input.active}`, {}), onSuccess: invalidate }),
  };
}

// ---------------------------------------------------------------- sign-in accounts (security §2.8: password + authenticator)

/** `GET /api/platform/accounts`; empty when the server does not serve it yet (before the strict sign-in), so the sheet hides the section. */
export function usePlatformAccounts(enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ["platform", "accounts"],
    enabled,
    staleTime: 15_000,
    retry: false,
    queryFn: async () => {
      try {
        return await api.get<PlatformAccount[]>("/api/platform/accounts");
      } catch (e) {
        if (e instanceof ApiError && (e.status === 404 || e.status === 403)) return null;
        throw e;
      }
    },
  });
}

export function useAccountActions() {
  const api = useApi();
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["platform", "accounts"] });
    void qc.invalidateQueries({ queryKey: ["platform", "users"] });
  };
  const base = (id: string) => `/api/platform/accounts/${encodeURIComponent(id)}`;
  return {
    // The account section shows a refusal beside the account (its own words for the second-administrator and self-change rules).
    issue: useSave({ mutationFn: (id: string) => api.post<TemporaryPassword>(`${base(id)}/credential`, {}), report: "silent", onSuccess: invalidate }),
    resetPassword: useSave({ mutationFn: (id: string) => api.post<TemporaryPassword>(`${base(id)}/reset-password`, {}), report: "silent", onSuccess: invalidate }),
    resetMfa: useSave({ mutationFn: (id: string) => api.post<void>(`${base(id)}/reset-mfa`, {}), report: "silent", onSuccess: invalidate }),
    unlock: useSave({ mutationFn: (id: string) => api.post<void>(`${base(id)}/unlock`, {}), report: "silent", onSuccess: invalidate }),
    signOut: useSave({ mutationFn: (id: string) => api.delete<void>(`${base(id)}/sessions`), report: "silent", onSuccess: invalidate }),
  };
}

// ---------------------------------------------------------------- API clients

export interface ApiClient {
  id: string;
  clientId: string;
  name: string;
  description?: string | null;
  secretHint: string;
  scopes: string[];
  serviceKeys: string[];
  ipAllowList: string[];
  tokenLifetimeMinutes: number;
  enabled: boolean;
  owner?: string | null;
  contactEmail?: string | null;
  lastUsedAt?: string | null;
  secretRotatedAt?: string | null;
  requestCount: number;
  webhookCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ApiClientInput {
  clientId?: string;
  name: string;
  description?: string | null;
  scopes: string[];
  serviceKeys: string[];
  ipAllowList: string[];
  tokenLifetimeMinutes: number;
  enabled: boolean;
  owner?: string | null;
  contactEmail?: string | null;
}

export interface ApiClientCredentials { client: ApiClient; clientSecret: string }

export function useApiClients() {
  const api = useApi();
  return useQuery({ queryKey: ["platform", "api-clients"], queryFn: () => api.get<ApiClient[]>("/api/platform/api-clients"), staleTime: 15_000 });
}

export function useApiScopes() {
  const api = useApi();
  return useQuery({ queryKey: ["platform", "api-clients", "scopes"], queryFn: () => api.get<PermissionInfo[]>("/api/platform/api-clients/scopes"), staleTime: 5 * 60_000 });
}

export function useApiClientActions() {
  const api = useApi();
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["platform", "api-clients"] });
  return {
    create: useSave({ mutationFn: (input: ApiClientInput) => api.post<ApiClientCredentials>("/api/platform/api-clients", input), onSuccess: invalidate }),
    update: useSave({ mutationFn: (input: { id: string } & ApiClientInput) => api.put<ApiClient>(`/api/platform/api-clients/${input.id}`, input), onSuccess: invalidate }),
    rotate: useSave({ mutationFn: (id: string) => api.post<ApiClientCredentials>(`/api/platform/api-clients/${id}/rotate`, {}), onSuccess: invalidate }),
    setEnabled: useSave({ mutationFn: (input: { id: string; enabled: boolean }) => api.post<ApiClient>(`/api/platform/api-clients/${input.id}/enabled`, { enabled: input.enabled }), onSuccess: invalidate }),
    remove: useSave({ mutationFn: (id: string) => api.delete<void>(`/api/platform/api-clients/${id}`), onSuccess: invalidate }),
  };
}

// ---------------------------------------------------------------- webhooks

export type WebhookDeliveryStatus = "Pending" | "Delivered" | "Failed" | "Exhausted";

export interface Webhook {
  id: string;
  key: string;
  name: string;
  url: string;
  events: string[];
  serviceKeys: string[];
  signingSecretRef?: string | null;
  signingSecretPresent: boolean;
  headers: Record<string, string>;
  enabled: boolean;
  maxAttempts: number;
  apiClientId?: string | null;
  apiClientName?: string | null;
  lastDeliveredAt?: string | null;
  lastFailedAt?: string | null;
  consecutiveFailures: number;
  pending: number;
  delivered24h: number;
  failed24h: number;
  createdAt: string;
  updatedAt: string;
}

export interface WebhookInput {
  key?: string;
  name: string;
  url: string;
  events: string[];
  serviceKeys: string[];
  signingSecretRef?: string | null;
  headers: Record<string, string>;
  enabled: boolean;
  maxAttempts: number;
  apiClientId?: string | null;
}

export interface WebhookDelivery {
  id: string;
  subscriptionId: string;
  subscriptionKey: string;
  eventType: string;
  eventId: string;
  caseId?: string | null;
  status: WebhookDeliveryStatus;
  attempts: number;
  lastStatusCode?: number | null;
  lastLatencyMs?: number | null;
  lastError?: string | null;
  nextAttemptAt?: string | null;
  deliveredAt?: string | null;
  createdAt: string;
  payloadJson?: string | null;
}

export interface WebhookEventType { key: string; description: string }

export function useWebhooks() {
  const api = useApi();
  return useQuery({ queryKey: ["platform", "webhooks"], queryFn: () => api.get<Webhook[]>("/api/platform/webhooks"), refetchInterval: 20_000 });
}

export function useWebhookEvents() {
  const api = useApi();
  return useQuery({ queryKey: ["platform", "webhooks", "events"], queryFn: () => api.get<WebhookEventType[]>("/api/platform/webhooks/events"), staleTime: 5 * 60_000 });
}

export function useWebhookDeliveries(subscriptionId: string | null, status: string | null) {
  const api = useApi();
  const qs = new URLSearchParams();
  if (subscriptionId) qs.set("subscriptionId", subscriptionId);
  if (status) qs.set("status", status);
  qs.set("take", "200");
  return useQuery({ queryKey: ["platform", "webhooks", "deliveries", subscriptionId, status], queryFn: () => api.get<WebhookDelivery[]>(`/api/platform/webhooks/deliveries?${qs.toString()}`), refetchInterval: 15_000 });
}

export function useWebhookDelivery(id: string | null) {
  const api = useApi();
  return useQuery({ queryKey: ["platform", "webhooks", "delivery", id], queryFn: () => api.get<WebhookDelivery>(`/api/platform/webhooks/deliveries/${id}`), enabled: !!id });
}

export function useWebhookActions() {
  const api = useApi();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["platform", "webhooks"] });
  return {
    create: useSave({ mutationFn: (input: WebhookInput) => api.post<Webhook>("/api/platform/webhooks", input), onSuccess: invalidate }),
    update: useSave({ mutationFn: (input: { id: string } & WebhookInput) => api.put<Webhook>(`/api/platform/webhooks/${input.id}`, input), onSuccess: invalidate }),
    setEnabled: useSave({ mutationFn: (input: { id: string; enabled: boolean }) => api.post<Webhook>(`/api/platform/webhooks/${input.id}/enabled`, { enabled: input.enabled }), onSuccess: invalidate }),
    test: useSave({ mutationFn: (id: string) => api.post<WebhookDelivery>(`/api/platform/webhooks/${id}/test`, {}), failed: t("admin:common.error"), onSuccess: invalidate }),
    redeliver: useSave({ mutationFn: (id: string) => api.post<WebhookDelivery>(`/api/platform/webhooks/deliveries/${id}/redeliver`, {}), onSuccess: invalidate }),
    remove: useSave({ mutationFn: (id: string) => api.delete<void>(`/api/platform/webhooks/${id}`), onSuccess: invalidate }),
  };
}

/** A published service – compiled procedure or playbook – for the "limit to services" pickers. */
export interface PlatformService {
  key: string;
  name: string;
  nameAr: string | null;
  code: string;
  kind: "compiled" | "playbook";
  workspaceKey: string | null;
  /** Outside systems may submit to it through the machine API (always true for compiled procedures). */
  acceptsExternal: boolean;
}

/** Served by both consoles, each under its own read permission. */
export function usePlatformServices(source: "api-clients" | "webhooks") {
  const api = useApi();
  return useQuery({ queryKey: ["platform", source, "services"], queryFn: () => api.get<PlatformService[]>(`/api/platform/${source}/services`), staleTime: 5 * 60_000 });
}
